// src/renderer/utils/handle-storage.ts

const DB_NAME = 'pixijs-editor-handles';
const DB_VERSION = 1;
const STORE_NAME = 'project-handles';

let dbInstance: IDBDatabase | null = null;

async function initHandleDB(): Promise<IDBDatabase> {
  if (dbInstance) return dbInstance;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };
  });
}

export async function saveProjectHandle(
  projectId: string,
  handle: FileSystemDirectoryHandle
): Promise<void> {
  const db = await initHandleDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(handle, projectId);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export async function getProjectHandle(
  projectId: string
): Promise<FileSystemDirectoryHandle | null> {
  const db = await initHandleDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(projectId);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || null);
  });
}

export async function removeProjectHandle(projectId: string): Promise<void> {
  const db = await initHandleDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(projectId);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export async function getAllProjectHandles(): Promise<Map<string, FileSystemDirectoryHandle>> {
  const db = await initHandleDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const result = new Map<string, FileSystemDirectoryHandle>();

    const cursorRequest = store.openCursor();
    cursorRequest.onerror = () => reject(cursorRequest.error);
    cursorRequest.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        result.set(cursor.key as string, cursor.value);
        cursor.continue();
      } else {
        resolve(result);
      }
    };
  });
}

export async function verifyPermission(
  handle: FileSystemDirectoryHandle,
  mode: FileSystemPermissionMode = 'readwrite'
): Promise<boolean> {
  try {
    const options: FileSystemHandlePermissionDescriptor = { mode };
    return (await handle.queryPermission(options)) === 'granted';
  } catch {
    // OPFS handles may not support queryPermission - always have permission
    return true;
  }
}

export async function requestPermission(
  handle: FileSystemDirectoryHandle,
  mode: FileSystemPermissionMode = 'readwrite'
): Promise<boolean> {
  try {
    const options: FileSystemHandlePermissionDescriptor = { mode };
    return (await handle.requestPermission(options)) === 'granted';
  } catch {
    // OPFS handles may not support requestPermission - always have permission
    return true;
  }
}

// Close database connection (useful for testing)
export function closeHandleDB(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

// ============================================================================
// TypeScript Declarations for File System Access API Permissions
// ============================================================================

declare global {
  type FileSystemPermissionMode = 'read' | 'readwrite';

  interface FileSystemHandlePermissionDescriptor {
    mode?: FileSystemPermissionMode;
  }

  interface FileSystemHandle {
    queryPermission(descriptor?: FileSystemHandlePermissionDescriptor): Promise<PermissionState>;
    requestPermission(descriptor?: FileSystemHandlePermissionDescriptor): Promise<PermissionState>;
  }
}
